import axios from "axios";

import {
  FoodControlBaseData,
  FoodControlPoint,
  FoodControlPointInstance,
  InstanceStatus,
} from "../../types/FoodManagementTypes";
import { dateToISO } from "../../utils/timeUtils";

export async function addControlPoint(controlPoint: Partial<FoodControlPoint>) {
  return axios
    .post<FoodControlPoint>(
      "/FoodControlPoints/addfoodcontrolpoint",
      controlPoint,
    )
    .then((res) => res.data);
}

export async function updateControlPoint(
  controlPoint: Partial<FoodControlPoint>,
) {
  return axios
    .put<FoodControlPoint>(
      "/FoodControlPoints/updatefoodcontrolpoint",
      controlPoint,
    )
    .then((res) => res.data);
}

export async function deleteControlPoint(id: string) {
  return axios
    .delete(`/FoodControlPoints/deletefoodcontrolpoint`, { data: { id } })
    .then((response) => response.data);
}

export async function deleteFoodControlPointInstance(id: string) {
  return axios.delete(
    `/FoodControlPointInstances/deletefoodcontrolpointinstance`,
    { data: { id } },
  );
}

export async function getFoodControlPointsFromLocationId(
  companyLocation: string,
) {
  return axios
    .get<FoodControlPoint[]>(`/FoodControlPoints/getcontrolpointbylocation`, {
      params: { companyLocation },
    })
    .then((res) => res.data);
}

export async function getFoodControlPointInstancesFromLocationId(
  CompanyLocationId: string,
  Status?: string,
) {
  return axios
    .get<FoodControlPointInstance[]>(
      `/FoodControlPointInstances/getallfoodcontrolpointinstancesbylocation `,
      {
        params: { CompanyLocationId, Status },
      },
    )
    .then((res) => res.data);
}

export async function getFoodControlPointInstancesFromDate(
  CompanyLocationId: string,
  StartDate: string,
  EndDate: string,
) {
  return axios
    .get<FoodControlPointInstance[]>(
      `/FoodControlPointInstances/getallfoodcontrolpointinstancesbydates `,
      {
        params: { CompanyLocationId, StartDate, EndDate },
      },
    )
    .then((res) => res.data);
}

export async function getFoodControlPointInstancesByCurrentDate(
  CompanyLocationId: string,
) {
  return axios
    .get<FoodControlPointInstance[]>(
      `/FoodControlPointInstances/getallfoodcontrolpointinstancesbycurrentdate `,
      {
        params: { CompanyLocationId },
      },
    )
    .then((res) => res.data);
}

export async function addFoodControlPointInstance(
  foodControlPointInstance: Omit<
    FoodControlPointInstance,
    "id" | "validationData"
  >,
) {
  return axios
    .post<FoodControlPointInstance>(
      `/FoodControlPointInstances/addfoodcontrolpointinstance`,
      {
        ...foodControlPointInstance,
        validationData: "1",
      },
    )
    .then((res) => res.data);
}

export async function updateFoodControlPointInstance(
  foodControlPointInstance: FoodControlPointInstance,
) {
  return axios
    .put(
      `/FoodControlPointInstances/updatefoodcontrolpointinstance`,
      foodControlPointInstance,
    )
    .then(() => foodControlPointInstance);
}

/*
if instance status is waiting, then it will be updated to completed
if instance status is completed, then it will create a new instance
if there are no instances instance data should be provided to create a new one
*/
export function withFoodControlPointInstance<T extends FoodControlBaseData>(
  update?: boolean,
) {
  return async (
    rest: T & {
      foodControlPointInstance: FoodControlPointInstance;
      reason?: string;
      remark?: string;
    },
  ) => {
    const { foodControlPointInstance, reason, remark, ...restData } = rest;

    const note = JSON.stringify({
      reason:
        restData.status === InstanceStatus.completedWithErrors ? reason : "[]",
      remark: rest.remark,
    });

    const newInstance: FoodControlPointInstance = {
      ...foodControlPointInstance,
      status: restData.status,
      reason:
        restData.status === InstanceStatus.completedWithErrors ? reason : "[]",
      remark: remark,
      updatedDate:
        rest.foodControlPointInstance.updatedDate ?? dateToISO(new Date()),
      data: JSON.stringify({
        ...restData,
        note,
        creationDate: dateToISO(new Date()),
      }),
    };

    if (
      rest.foodControlPointInstance.status === InstanceStatus.waiting ||
      update
    )
      return updateFoodControlPointInstance(newInstance);

    return addFoodControlPointInstance(newInstance);
  };
}
